"use strict";

const EMPTY = "-";

const CONFIRM_ACTIVITY_TYPES = Object.freeze([
  "SUPPLIER_ACKNOWLEDGE",
  "PURCHASING_CONFIRM",
  "PURCHASING_PROXY_COMPLETE",
]);

const SUPPLIER_CONFIRM_ACTIVITY_TYPE = "SUPPLIER_ACKNOWLEDGE";

const PROXY_CONFIRM_ACTIVITY_TYPES = Object.freeze([
  "PURCHASING_CONFIRM",
  "PURCHASING_PROXY_COMPLETE",
]);

const SEND_ACTIVITY_TYPE = "PURCHASING_SEND";
const JAKARTA_TIME_ZONE = "Asia/Jakarta";

const STATUS_LABELS = Object.freeze({
  O: "Outstanding",
  B: "Sent",
  P: "Completed",
  R: "Rejected",
  D: "Dismissed",
  PD: "Pending Dismiss",
});

/**
 * Parse `key: value` segments from activity description
 * (format produced by buildActivityDescription).
 */
const parseActivityDescriptionField = (description, fieldKey) => {
  if (!description || !fieldKey) return null;
  const escaped = String(fieldKey).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const match = String(description).match(
    new RegExp(`(?:^|\\|\\s*)${escaped}:\\s*([^|]+)`, "i"),
  );
  if (!match) return null;
  const value = String(match[1] || "").trim();
  return value || null;
};

const formatActivityDate = (value) => {
  if (!value) return EMPTY;
  if (typeof value === "string") {
    const naive = value.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2})/);
    if (naive && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(value)) {
      return `${naive[1]} ${naive[2]}`;
    }
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: JAKARTA_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(parsed);
  const get = (type) => parts.find((part) => part.type === type)?.value || "00";
  return `${get("year")}-${get("month")}-${get("day")} ${get("hour")}:${get("minute")}:${get("second")}`;
};

/** Prefer user_id from activity description (legacy rows without created_by). */
const resolveActorFromDescription = (description) =>
  parseActivityDescriptionField(description, "user_id") ||
  parseActivityDescriptionField(description, "confirm_by");

/** Prefer created_by (user id); fall back to description for legacy rows. */
const resolveActivityActor = (row) => {
  const displayName = row?.created_by_name != null ? String(row.created_by_name).trim() : "";
  if (displayName) return displayName;
  const createdBy =
    row?.created_by != null ? String(row.created_by).trim() : "";
  if (createdBy) return createdBy;
  return resolveActorFromDescription(row?.description);
};

const resolveSendToSupplierName = (activities = []) => {
  const list = Array.isArray(activities) ? activities : [];
  for (let i = list.length - 1; i >= 0; i -= 1) {
    const row = list[i];
    if (row?.type !== SEND_ACTIVITY_TYPE) continue;
    const fromName =
      (row?.created_by_name != null ? String(row.created_by_name).trim() : "") ||
      parseActivityDescriptionField(row?.description, "display_name");
    if (fromName) return fromName;
    const actor = resolveActivityActor(row);
    if (actor) return actor;
  }
  return EMPTY;
};

const resolveSendDate = (activities = [], scoringRow = null) => {
  const list = Array.isArray(activities) ? activities : [];
  for (let i = list.length - 1; i >= 0; i -= 1) {
    const row = list[i];
    if (row?.type === SEND_ACTIVITY_TYPE && row?.activity_date) {
      return formatActivityDate(row.activity_date);
    }
  }
  const status = scoringRow?.status;
  if (status && status !== "O" && scoringRow?.last_updated_date) {
    return formatActivityDate(scoringRow.last_updated_date);
  }
  return EMPTY;
};

const resolveConfirmTime = (activities = []) => {
  const list = Array.isArray(activities) ? activities : [];
  const confirmTypes = new Set(CONFIRM_ACTIVITY_TYPES);
  const match = list.find(
    (row) => confirmTypes.has(row?.type) && row?.activity_date,
  );
  if (!match) return EMPTY;
  return formatActivityDate(match.activity_date);
};

/**
 * Split confirm actor by path:
 * - SUPPLIER_ACKNOWLEDGE → Supplier Confirm (created_by)
 * - PURCHASING_CONFIRM / PURCHASING_PROXY_COMPLETE → Proxy yang Confirm
 */
const resolveConfirmActors = (activities = []) => {
  const list = Array.isArray(activities) ? activities : [];
  const proxyTypes = new Set(PROXY_CONFIRM_ACTIVITY_TYPES);

  let supplierConfirmBy = EMPTY;
  let proxyConfirmBy = EMPTY;

  for (let i = list.length - 1; i >= 0; i -= 1) {
    const row = list[i];
    if (!row?.type) continue;
    if (
      supplierConfirmBy === EMPTY &&
      row.type === SUPPLIER_CONFIRM_ACTIVITY_TYPE
    ) {
      supplierConfirmBy = resolveActivityActor(row) || EMPTY;
    }
    if (proxyConfirmBy === EMPTY && proxyTypes.has(row.type)) {
      proxyConfirmBy = resolveActivityActor(row) || EMPTY;
    }
    if (supplierConfirmBy !== EMPTY && proxyConfirmBy !== EMPTY) break;
  }

  return {
    supplier_confirm_by: supplierConfirmBy,
    proxy_confirm_by: proxyConfirmBy,
    confirm_time: resolveConfirmTime(list),
  };
};

const statusLabelForExport = (status) =>
  STATUS_LABELS[status] || status || EMPTY;

const DISMISS_STATUSES = Object.freeze(new Set(["D", "PD"]));

const DISMISS_REQUEST_ACTIVITY_TYPE = "PURCHASING_DISMISS_REQUEST";

/**
 * Resolve dismiss actor + date from scoring / export-report pivot fields.
 * - Dismissed By/Date: purchasing dismiss request (or legacy last_updated_*).
 * - Approver Dismiss By/Date: only dismiss_approved_by / dismiss_approved_date
 *   (APPROVER_DISMISS_APPROVE from export-report pivot).
 * - Dismiss Rejected By/Date: dismiss_rejected_* (APPROVER_DISMISS_REJECT).
 */
const resolveDismissFields = (row = {}) => {
  const status = row?.status;
  const requestedByName =
    row?.dismiss_requested_by_name != null
      ? String(row.dismiss_requested_by_name).trim()
      : "";
  const pivotRequestBy =
    row?.dismiss_request_by != null ? String(row.dismiss_request_by).trim() : "";
  const requestedBy =
    row?.dismiss_requested_by != null
      ? String(row.dismiss_requested_by).trim()
      : "";
  const decidedBy =
    row?.dismiss_decided_by != null
      ? String(row.dismiss_decided_by).trim()
      : "";
  // Pivot from export-report: APPROVER_DISMISS_APPROVE → dismiss_approved_*
  const pivotApprovedBy =
    row?.dismiss_approved_by != null ? String(row.dismiss_approved_by).trim() : "";
  const pivotRejectedBy =
    (row?.dismiss_rejected_by != null ? String(row.dismiss_rejected_by).trim() : "") ||
    (row?.dismiss_reject_by != null ? String(row.dismiss_reject_by).trim() : "");
  const legacyBy =
    DISMISS_STATUSES.has(status) && row?.last_updated_by != null
      ? String(row.last_updated_by).trim()
      : "";

  const dismissedBy =
    requestedByName ||
    pivotRequestBy ||
    requestedBy ||
    decidedBy ||
    legacyBy ||
    EMPTY;

  const rawDate =
    row?.dismiss_request_date ||
    row?.dismiss_decided_at ||
    row?.dismiss_requested_at ||
    (DISMISS_STATUSES.has(status) ? row?.last_updated_date : null);
  const dismissedDate = rawDate ? formatActivityDate(rawDate) : EMPTY;

  // Approver Dismiss By/Date: only APPROVER_DISMISS_APPROVE pivot (not reject/legacy decided)
  const approverBy = pivotApprovedBy || EMPTY;
  const approverRawDate =
    pivotApprovedBy && row?.dismiss_approved_date
      ? row.dismiss_approved_date
      : null;
  const approverDate = approverBy && approverRawDate
    ? formatActivityDate(approverRawDate)
    : EMPTY;

  const rejectedBy = pivotRejectedBy || EMPTY;
  const rejectedRawDate =
    row?.dismiss_rejected_date || row?.dismiss_reject_date || null;
  const rejectedDate =
    rejectedBy && rejectedRawDate ? formatActivityDate(rejectedRawDate) : EMPTY;

  return {
    dismissed_by: dismissedBy,
    dismissed_date: dismissedDate,
    approver_dismiss_by: approverBy,
    approver_dismiss_date: approverDate,
    dismiss_rejected_by: rejectedBy,
    dismiss_rejected_date: rejectedDate,
  };
};

const resolveDismissRequestedByName = (activities = []) => {
  const list = Array.isArray(activities) ? activities : [];
  for (let i = list.length - 1; i >= 0; i -= 1) {
    const row = list[i];
    if (row?.type !== DISMISS_REQUEST_ACTIVITY_TYPE) continue;
    const fromName =
      (row?.created_by_name != null ? String(row.created_by_name).trim() : "") ||
      parseActivityDescriptionField(row?.description, "display_name");
    if (fromName) return fromName;
  }
  return "";
};

const mapSpeRowToExcel = (row) => {
  const dismiss = resolveDismissFields(row);
  const semesterRaw = row.semester;
  const semesterLabel =
    semesterRaw == null || semesterRaw === ""
      ? EMPTY
      : String(semesterRaw).startsWith("S")
        ? String(semesterRaw)
        : `S${semesterRaw}`;

  const sendBy = row.purchasing_send_by || row.send_to_supplier_name || EMPTY;
  const sendDateRaw = row.purchasing_send_date || row.send_to_supplier_date;
  const supplierConfirm =
    row.supplier_acknowledge_by || row.supplier_confirm_by || EMPTY;
  const proxyConfirm = row.proxy_confirmed_by || row.proxy_confirm_by || EMPTY;
  const confirmTimeRaw =
    row.supplier_acknowledge_date ||
    row.proxy_confirmed_date ||
    row.confirm_time ||
    null;

  return {
    Year: row.year ?? EMPTY,
    Semester: semesterLabel,
    "Supplier Name": row.supplier_name || EMPTY,
    Category: row.category || EMPTY,
    Method: row.method || EMPTY,
    Score:
      row.supplier_score != null && row.supplier_score !== ""
        ? row.supplier_score
        : EMPTY,
    "%Score":
      row.percent_score != null && row.percent_score !== ""
        ? row.percent_score
        : EMPTY,
    Status: statusLabelForExport(row.status),
    Acknowledged:
      row.acknowledged === true || row.acknowledged === 1
        ? "Yes"
        : row.acknowledged === false || row.acknowledged === 0
        ? "No"
        : supplierConfirm !== EMPTY
          ? "Yes"
          : EMPTY,
    "Source Tab": row.source_tab || statusLabelForExport(row.status) || EMPTY,
    "Send By": sendBy,
    "Send Date": sendDateRaw ? formatActivityDate(sendDateRaw) : EMPTY,
    "Supplier Confirm": supplierConfirm,
    "Proxy Confirm": proxyConfirm,
    "Confirm Time": confirmTimeRaw ? formatActivityDate(confirmTimeRaw) : EMPTY,
    "Dismissed By": dismiss.dismissed_by,
    "Dismissed Date": dismiss.dismissed_date,
    "Approver Dismiss By": dismiss.approver_dismiss_by,
    "Approver Dismiss Date": dismiss.approver_dismiss_date,
    "Dismiss Rejected By": dismiss.dismiss_rejected_by,
    "Dismiss Rejected Date": dismiss.dismiss_rejected_date,
  };
};

const dedupeByScoringId = (rows = []) => {
  const seen = new Set();
  const result = [];
  for (const row of rows) {
    const id = row?.scoring_id;
    if (id == null || seen.has(id)) continue;
    seen.add(id);
    result.push(row);
  }
  return result;
};

module.exports = {
  EMPTY,
  CONFIRM_ACTIVITY_TYPES,
  SUPPLIER_CONFIRM_ACTIVITY_TYPE,
  PROXY_CONFIRM_ACTIVITY_TYPES,
  SEND_ACTIVITY_TYPE,
  DISMISS_REQUEST_ACTIVITY_TYPE,
  STATUS_LABELS,
  DISMISS_STATUSES,
  parseActivityDescriptionField,
  formatActivityDate,
  resolveActorFromDescription,
  resolveActivityActor,
  resolveSendToSupplierName,
  resolveSendDate,
  resolveConfirmTime,
  resolveConfirmActors,
  resolveDismissFields,
  resolveDismissRequestedByName,
  statusLabelForExport,
  mapSpeRowToExcel,
  dedupeByScoringId,
};
